import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import ApiClient from '../../utils/ApiClient';

// Lifecycle states come from MfnReturnsService. The two exception states are the queue
// somebody actually works; the rest are context.
const STATE_STYLES = {
    DELIVERED:      'bg-green-100 text-green-800',
    IN_TRANSIT:     'bg-blue-100 text-blue-800',
    AUTHORIZED:     'bg-slate-100 text-slate-700',
    REQUESTED:      'bg-slate-100 text-slate-700',
    CLOSED:         'bg-slate-100 text-slate-500',
    NOT_DELIVERED:  'bg-red-100 text-red-800',
    REFUNDED_SHORT: 'bg-red-100 text-red-800',
};

const STATE_LABELS = {
    DELIVERED:      'Delivered',
    IN_TRANSIT:     'In transit',
    AUTHORIZED:     'Authorized',
    REQUESTED:      'Requested',
    CLOSED:         'Closed',
    NOT_DELIVERED:  'Not delivered',
    REFUNDED_SHORT: 'Refunded, not back',
};

const FILTERS = [
    { key: 'EXCEPTIONS', label: 'Needs action' },
    { key: 'IN_TRANSIT', label: 'In transit' },
    { key: 'DELIVERED', label: 'Delivered' },
    { key: 'ALL', label: 'All' },
];

function StateChip({ state }) {
    const style = STATE_STYLES[state] || 'bg-slate-100 text-slate-700';
    return (
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${style}`}>
            {STATE_LABELS[state] || state}
        </span>
    );
}

// The SAFE-T filing window for US seller-fulfilled orders is 30 days as of 2026-02-16,
// so this countdown is the thing worth looking at first.
function ClaimWindow({ row }) {
    if (row.safet_claim_id) {
        return <span className="text-xs text-slate-500">Claim filed</span>;
    }
    if (row.return_delivery_date || row.received_at) {
        return <span className="text-xs text-slate-400">&mdash;</span>;
    }
    const days = row.days_to_safet_deadline;
    if (days === null || days === undefined) {
        return <span className="text-xs text-slate-400">&mdash;</span>;
    }
    if (days < 0) {
        return <span className="text-xs font-semibold text-slate-500">Expired</span>;
    }
    const urgent = days <= 7;
    return (
        <span className={`text-xs font-semibold ${urgent ? 'text-red-700' : 'text-slate-700'}`}>
            {days} {days === 1 ? 'day' : 'days'} left
        </span>
    );
}

function money(value) {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    return Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`;
}

function day(value) {
    if (!value) return '—';
    return String(value).slice(0, 10);
}

function MerchantReturns() {
    const [filter, setFilter] = useState('EXCEPTIONS');
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (state) => {
        setLoading(true);
        try {
            const response = await ApiClient.get('/api/mfn-returns', { params: { state, days: 120 } });
            setRows(response.data?.returns || []);
            setCounts(response.data?.counts || {});
        } catch (error) {
            console.error('Error loading merchant returns:', error);
            toast.error('Could not load merchant returns.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(filter); }, [filter, load]);

    // Ask Amazon for a fresh report rather than waiting for tomorrow's 05:06 job.
    const refreshFromAmazon = async () => {
        setRefreshing(true);
        try {
            await ApiClient.post('/api/mfn-returns/request-report');
            toast.success('Report requested. New returns appear within a few minutes.');
        } catch (error) {
            console.error('Error requesting returns report:', error);
            toast.error('Could not request the report from Amazon.');
        } finally {
            setRefreshing(false);
        }
    };

    const markReceived = async (row) => {
        try {
            const response = await ApiClient.post(`/api/mfn-returns/${row.id}/received`, {});
            if (response.data?.updated) {
                toast.success('Marked received.');
                load(filter);
            } else {
                toast(response.data?.message || 'Already marked received.');
            }
        } catch (error) {
            console.error('Error marking return received:', error);
            toast.error('Could not mark it received.');
        }
    };

    const needsAction = useMemo(
        () => (counts.NOT_DELIVERED || 0) + (counts.REFUNDED_SHORT || 0),
        [counts]
    );

    const summary = [
        { label: 'Needs action', value: needsAction, tone: needsAction > 0 ? 'text-red-700' : 'text-green-700' },
        { label: 'In transit', value: counts.IN_TRANSIT || 0, tone: 'text-blue-700' },
        { label: 'Delivered', value: counts.DELIVERED || 0, tone: 'text-green-700' },
        { label: 'Open requests', value: (counts.REQUESTED || 0) + (counts.AUTHORIZED || 0), tone: 'text-slate-700' },
    ];

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">Merchant-fulfilled returns</h2>
                    <p className="mt-1 text-sm text-slate-500">
                        Customer returns on MFN orders, from Amazon&rsquo;s returns report. Refreshed every
                        morning; a return&rsquo;s delivery scan can arrive weeks after the request.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refreshFromAmazon}
                    disabled={refreshing}
                    className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                    {refreshing ? 'Requesting…' : 'Refresh from Amazon'}
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {summary.map((card) => (
                    <div key={card.label} className="rounded border border-slate-200 bg-white px-4 py-3">
                        <div className={`text-2xl font-bold tabular-nums ${card.tone}`}>{card.value}</div>
                        <div className="mt-0.5 text-xs uppercase tracking-wide text-slate-500">{card.label}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={`rounded px-3 py-1.5 text-sm font-medium ${
                            filter === f.key
                                ? 'bg-slate-900 text-white'
                                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            {['Status', 'Order', 'Item', 'Reason', 'Requested', 'Age', 'Delivered back', 'Refunded', 'Claim window', ''].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && (
                            <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">Loading&hellip;</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                                    {filter === 'EXCEPTIONS'
                                        ? 'Nothing needs attention. Every return is either on its way or back with us.'
                                        : 'No returns in this view.'}
                                </td>
                            </tr>
                        )}
                        {!loading && rows.map((row) => (
                            <tr key={row.id} className="hover:bg-slate-50">
                                <td className="px-3 py-2 whitespace-nowrap"><StateChip state={row.lifecycle_state} /></td>
                                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{row.amazon_order_id}</td>
                                <td className="px-3 py-2 max-w-xs truncate text-slate-800" title={row.item_name || row.order_product_name}>
                                    {row.item_name || row.order_product_name || '—'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{row.return_reason || '—'}</td>
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-slate-600">{day(row.return_request_date)}</td>
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-slate-600">
                                    {row.days_since_request === null || row.days_since_request === undefined ? '—' : `${row.days_since_request}d`}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-slate-600">
                                    {day(row.return_delivery_date || row.received_at)}
                                    {row.received_at && !row.return_delivery_date && (
                                        <span className="ml-1 text-xs text-slate-400">(scanned)</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-slate-700">{money(row.refunded_amount)}</td>
                                <td className="px-3 py-2 whitespace-nowrap"><ClaimWindow row={row} /></td>
                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                    {!row.return_delivery_date && !row.received_at && (
                                        <button
                                            type="button"
                                            onClick={() => markReceived(row)}
                                            className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                                        >
                                            Mark received
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-slate-500">
                &ldquo;Mark received&rdquo; records that the unit physically reached us. Use it when the buyer
                shipped on their own label &mdash; Amazon never sends a delivery scan for those.
            </p>
        </div>
    );
}

export default MerchantReturns;
